import type { DisplayUnit } from './types.js';

const FACTOR: Record<DisplayUnit, number> = { mm: 1000, cm: 100, m: 1 };
const DECIMALS: Record<DisplayUnit, number> = { mm: 0, cm: 1, m: 2 };

/** `Intl` tag for displayed numbers ("4,82 m" in German and Polish, "4.82 m" in English). */
let numberLocale = 'de-DE';
const formats = new Map<string, Intl.NumberFormat>();

/** Switches the number format of all lengths and areas; the app calls it on language change. */
export function setNumberLocale(tag: string): void {
  numberLocale = tag;
}

export const getNumberLocale = (): string => numberLocale;

const numberFormat = (decimals: number) => {
  const key = `${numberLocale}|${String(decimals)}`;
  let format = formats.get(key);
  if (!format) {
    format = new Intl.NumberFormat(numberLocale, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
    formats.set(key, format);
  }
  return format;
};

/** Plain number in the current display language, e.g. 1234.5 → "1.234,5" (de) / "1,234.5" (en). */
export function formatNumber(value: number, maxDecimals = 2, minDecimals = 0): string {
  return new Intl.NumberFormat(numberLocale, {
    minimumFractionDigits: minDecimals,
    maximumFractionDigits: maxDecimals,
  }).format(value);
}

/** Ratio as percent in the current display language, e.g. 0.125 → "12,5 %". */
export function formatPercentValue(ratio: number, maxDecimals = 1, minDecimals = 0): string {
  return new Intl.NumberFormat(numberLocale, {
    style: 'percent',
    minimumFractionDigits: minDecimals,
    maximumFractionDigits: maxDecimals,
  }).format(ratio);
}

/** Formats a model length (meters) for display, e.g. 4.82 → "4,82 m". */
export function formatLength(
  meters: number,
  unit: DisplayUnit = 'm',
  decimals: number = DECIMALS[unit],
): string {
  return `${numberFormat(decimals).format(meters * FACTOR[unit])} ${unit}`;
}

/** Areas are always shown in m², the unit planners think in. */
export function formatArea(squareMeters: number, decimals = 0): string {
  return `${numberFormat(decimals).format(squareMeters)} m²`;
}

/**
 * Parses user input in the given display unit into meters. Accepts German and English
 * decimal separators and an optional unit suffix that overrides the default unit.
 * Returns null for anything that is not a finite number.
 */
export function parseLength(input: string, defaultUnit: DisplayUnit = 'm'): number | null {
  const match = /^\s*(-?\d+(?:[.,]\d+)?)\s*(mm|cm|m)?\s*$/i.exec(input);
  if (!match?.[1]) return null;
  const value = Number(match[1].replace(',', '.'));
  if (!Number.isFinite(value)) return null;
  const unit = (match[2]?.toLowerCase() as DisplayUnit | undefined) ?? defaultUnit;
  return value / FACTOR[unit];
}
